import { ApiError } from '@/api/envelope';
import type { ArcItemResponse, ArcResponse, ArcSectionResponse } from '@/api/generated';
import type { StatusTone } from '@/components/ui/StatusChip';

/*
 * The ARC as the builder reads it, and the words for its codes.
 *
 * The backend sends its codes as plain strings (the generated types say
 * `string`), so every lookup here falls back to something readable for a code
 * the portal has not met, rather than a blank.
 */

export type Arc = ArcResponse;
export type ArcSection = ArcSectionResponse;
export type ArcItem = ArcItemResponse;

/** What the Donate and Volunteer switches are called in a request and a reply. */
export type HeaderAction = 'donate' | 'volunteer';

/** A standard section's type, the words for its content, and its Add button. */
interface StandardSection {
  type: string;
  /** Plural, lower case, for "No campaigns yet". */
  plural: string;
  addLabel: string;
}

/** The six standard sections in the order the backend creates them. */
export const STANDARD_SECTIONS: StandardSection[] = [
  { type: 'FEATURED', plural: 'featured content', addLabel: 'Add Content' },
  { type: 'CAMPAIGNS', plural: 'campaigns', addLabel: 'Add Campaign' },
  { type: 'GUIDES', plural: 'guides', addLabel: 'Add Guide' },
  { type: 'SURVEYS', plural: 'surveys', addLabel: 'Add Survey' },
  { type: 'PAGES', plural: 'pages', addLabel: 'Add Page' },
  { type: 'DOCUMENTS', plural: 'documents', addLabel: 'Add Document' },
];

function standard(section: ArcSection): StandardSection | undefined {
  return section.standard_type
    ? STANDARD_SECTIONS.find((entry) => entry.type === section.standard_type)
    : undefined;
}

/** "Add campaign" for a standard section, "Add content" for Featured and custom. */
export function addLabel(section: ArcSection): string {
  return standard(section)?.addLabel ?? 'Add Content';
}

/** What an empty section's table says. */
export function emptyWords(section: ArcSection): string {
  const entry = standard(section);
  if (!entry || entry.type === 'FEATURED') return 'Nothing in this section yet';
  return `No ${entry.plural} yet`;
}

const ARC_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  PUBLISHED: { label: 'Published', tone: 'success' },
  UNPUBLISHED: { label: 'Unpublished', tone: 'warning' },
};

/** The ARC's own status as a chip. */
export function arcStatus(status: string): { label: string; tone: StatusTone } {
  return ARC_STATUS[status] ?? { label: status, tone: 'neutral' };
}

/**
 * A placed item's status as a chip. An item whose resource has gone (deleted,
 * unpublished, or another organization's) reads "Unavailable"; otherwise the
 * resource's own label, green when the app can show it.
 */
export function itemStatus(item: ArcItem): { label: string; tone: StatusTone } {
  if (!item.available || !item.resource) return { label: 'Unavailable', tone: 'error' };
  return {
    label: item.resource.status_label,
    tone: item.resource.is_published ? 'success' : 'warning',
  };
}

export const RESOURCE_TYPE_LABELS: Record<string, string> = {
  CAMPAIGN: 'Campaign',
  GUIDE: 'Guide',
  SURVEY: 'Survey',
  PAGE: 'Page',
  DOCUMENT: 'Document',
  // Named on Day 27; their modules arrive with E1 and P1.
  PETITION: 'Petition',
  EVENT: 'Event',
};

export function resourceTypeLabel(type: string): string {
  return RESOURCE_TYPE_LABELS[type] ?? type;
}

/** The state of the link a header button opens, shared with the links sub-pages (P11). */
export { linkStatusLabel } from '@/lib/linkStatus';

/** The link status a switch needs before it can be changed (group 6, D4). */
export const LINK_PUBLISHED = 'PUBLISHED';

/** What the Volunteer button opens, from its mode. */
export function volunteerHint(mode: string): string {
  return mode === 'NATIVE' ? 'In-app recruitment form' : 'External volunteer form';
}

/** "0 items", "1 item", "3 items". */

/** The legend's dots: blue for one kind of content, purple for mixed (the wireframe). */
export const KIND_COLOURS = { standard: '#90CAF9', mixed: '#B39DDB' } as const;

/** "3 Items", as a section's header in the wireframe counts them. */
export function itemCountLabel(count: number): string {
  return `${String(count)} ${count === 1 ? 'Item' : 'Items'}`;
}

/**
 * What a section holds, in the wireframe's legend (Part 2, page 4): one kind
 * of content is a Standard Section; more than one - Featured, and every
 * custom section - is mixed, and reads as a Custom Section.
 */
export function contentKind(section: ArcSection): { label: string; mixed: boolean } {
  const mixed = section.kind !== 'STANDARD' || section.accepts.length > 1;
  return { label: mixed ? 'Custom Section' : 'Standard Section', mixed };
}

/** The title an item is known by, even when its resource has gone. */
export function itemTitle(item: ArcItem): string {
  return item.resource?.title ?? 'No longer available';
}

/** The sections in the order the app shows them. */
export function inOrder(sections: ArcSection[]): ArcSection[] {
  return [...sections].sort((a, b) => a.position - b.position);
}

/** The server's own sentence for a refusal, shared with the links sub-pages (P11). */
export { serverSentence } from '@/lib/serverSentence';

/**
 * True when the backend refused the request (4xx): sending the same thing
 * again gives the same answer. A fault (5xx) or no reply at all is not a
 * refusal, so asking again may work.
 */
export function isRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.isClientError;
}
